import type { CampaignOperations, CampaignOperationsFields } from "@/marketing/models/campaignOperations";

export type CampaignTrackerCellValue = string | boolean | null;

/** Builds one-field operations patches and reads stored values by the same dotted path. */
export class CampaignOperationsPatches {
  public static forField(field: string, value: CampaignTrackerCellValue): CampaignOperationsFields {
    const normalized = typeof value === "string" ? value.trim() || null : value;
    const keys = field.split(".");
    let patch: unknown = normalized;
    for (let index = keys.length - 1; index >= 0; index -= 1) {
      patch = { [keys[index] as string]: patch };
    }
    return patch as CampaignOperationsFields;
  }

  /** Exact stored value under a path, untrimmed; undefined becomes null so the key is cleared. Used to undo a cell. */
  public static forStoredValue(field: string, value: unknown): CampaignOperationsFields {
    let patch: unknown = value === undefined ? null : value;
    const keys = field.split(".");
    for (let index = keys.length - 1; index >= 0; index -= 1) {
      patch = { [keys[index] as string]: patch };
    }
    return patch as CampaignOperationsFields;
  }

  /** Same merge rule as the server (omitted keys stay, null clears) without history, for optimistic display. */
  public static applyLocally(operations: CampaignOperations | undefined, patch: CampaignOperationsFields): CampaignOperations {
    return CampaignOperationsPatches.merge({ ...(operations ?? {}) }, patch as Record<string, unknown>) as CampaignOperations;
  }

  private static merge(stored: Record<string, unknown>, patch: Record<string, unknown>): Record<string, unknown> {
    const merged: Record<string, unknown> = { ...stored };
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) continue;
      if (value === null) {
        delete merged[key];
        continue;
      }
      if (typeof value === "object" && !Array.isArray(value)) {
        const storedGroup = merged[key];
        const nested = CampaignOperationsPatches.merge(
          storedGroup !== null && typeof storedGroup === "object" ? (storedGroup as Record<string, unknown>) : {},
          value as Record<string, unknown>,
        );
        if (Object.keys(nested).length === 0) delete merged[key];
        else merged[key] = nested;
        continue;
      }
      merged[key] = value;
    }
    return merged;
  }

  public static read(operations: CampaignOperations | undefined, field: string): unknown {
    let current: unknown = operations;
    for (const key of field.split(".")) {
      if (current === null || typeof current !== "object") return undefined;
      current = (current as Record<string, unknown>)[key];
    }
    return current;
  }
}
